import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus, 
  faMagnifyingGlass, 
  faWrench,
  faPencil,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import SideBar from '../../SideBar'
import ClaimSlideOut from './Components/ClaimSlideOut'
import EditClaimSlideOut from './Components/EditClaimSlideOut'
import useAxiosPrivateSAI from '../../../hooks/useAxiosPrivateSAI'
import useAuth from '../../../hooks/useAuth'
import { useTheme } from '../../../context/ThemeProvider'
import moment from 'moment'
import {toast} from 'sonner'
import { Tooltip } from 'react-tooltip'

const Claims = () => {
  const { setShowLoader } = useAuth()
  const theme = useTheme()
  const style = {
    backgroundColor: theme.backgroundColor,
    color: theme.textColor,
  }
  const [policyData, setPolicyData] = useState(null)
  const axios = useAxiosPrivateSAI()
  const [activeTab, setActiveTab] = useState('current')
  const [claims, setClaims] = useState([])
  const [claim, setClaim] = useState([])
  const [toggleClaim, setToggleClaim] = useState(false)
  const [open, setOpen] = useState(false)
  const [viewOpenClaims, setViewOpenClaims] = useState(true)
  const [showOld, setShowOld] = useState(false)
  const [canClaim, setCanClaim] = useState(false)
  const [selectedPreviousPolicy, setSelectedPreviousPolicy] = useState(null)
  const [viewOld, setViewOld] = useState(false)
  const [previousClaims, setPreviousClaims] = useState([])
  const [daysLeftToClaim, setDaysLeftToClaim] = useState(null)
  const [policyStatus, setPolicyStatus] = useState('')
  const [canClaimPrevious, setCanClaimPrevious] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchParams] = useSearchParams()
  const actionClaimId = searchParams.get('actionClaimId')
  const [isLoading, setIsLoading] = useState(false)

  const openClaim = (claim) => {
    setOpen(true)
    setClaim(claim)
  }

  const openEdit = (claim) => {
    setToggleClaim(true)
    setClaim(claim)
  }

  useEffect(() => {
    if (actionClaimId && claims?.length > 0) {
      const foundClaim = claims?.find(claim => claim?.id === Number(actionClaimId))
      if (foundClaim) {
        setClaim(foundClaim)
        setOpen(true)
      }
    }
  }, [actionClaimId, claims])  

  const statusColors = {
    // Red = urgent/problem
    3: "bg-red-100 text-red-700",   // Client Action Required – Doc
    4: "bg-red-100 text-red-700",   // Client Action Required – RTW
    8: "bg-red-100 text-red-700", // No Cover
    9: "bg-red-100 text-red-700", // Late Reported
    11: "bg-red-100 text-red-700", // Declined

    // Orange = pending / in progress
    1: "bg-orange-100 text-orange-700", // Unsubmitted
    2: "bg-orange-100 text-orange-700", // Review
    18: "bg-orange-100 text-orange-700", // Awaiting Authorisation
    19: "bg-orange-100 text-orange-700", // Awaiting Decline
    23: "bg-orange-100 text-orange-700", // Covid Claim - Ready to Process
  
    // Blue = neutral process states
    5: "bg-blue-100 text-blue-700",  // Interim Payment
    6: "bg-blue-100 text-blue-700", // Account to Settle
    14: "bg-blue-100 text-blue-700", // Recalculation
    15: "bg-blue-100 text-blue-700", // Transferred to new policy
    16: "bg-blue-100 text-blue-700", // Maternity RTW
    21: "bg-blue-100 text-blue-700", // Mutual - Recalculation
  
    // Green = completed/paid
    7: "bg-green-100 text-green-700", // Paid - full and final
    20: "bg-green-100 text-green-700", // Paid in full
    25: "bg-green-100 text-green-700", // Paid - Full and Final
    26: "bg-green-100 text-green-700", // Account to Settle - Transfer
  
    // Purple = declined/closed
    10: "bg-purple-100 text-purple-700", // Outstanding Docs
    12: "bg-purple-100 text-purple-700", // Awaiting Payment (premium issue)
    13: "bg-purple-100 text-purple-700", // Awaiting GP Reply
    17: "bg-purple-100 text-purple-700", // Under Excess
    22: "bg-purple-100 text-purple-700", // Awaiting Docs
    24: "bg-purple-100 text-purple-700", // Transferred to Mutual
  };

  const getStatusTag = (statusId, statusText) => {
    const colorClass = statusColors[statusId] || "bg-gray-100 text-gray-700";
    return (
      <span
        className={`text-[10px] font-semibold px-2 py-1 rounded capitalize ${colorClass}`}
      >
        {statusText}
      </span>
    );
  };

  const fetchClaims = async () => {
    try {
      setIsLoading(true)
      const claimsRes = await axios.get(`/api/claims/${policyData.policy_id}/list/${viewOpenClaims}?q=&l=10&o=0`);
      setClaims(claimsRes.data || [])
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setClaims([]);
    }
    setIsLoading(false)
  }

  const fetchPreviousClaims = async () => {
    try {
      const res = await axios.get(
        `/api/claims/${policyData.policy_id}/old/${viewOpenClaims}?q=&l=10&o=0`
      );
      setPreviousClaims(res.data || [])
    } catch (error) {
      console.error('Error fetching previous claims:', error)
      setPreviousClaims([])
    }
  }

  const canStillClaim = async (selectedPolicyId) => {
    try {
      setShowLoader(true)
      const response = await axios.get(`/api/claims/${selectedPolicyId}/can-still-claim`)
      const data = response.data

      setShowOld(data.show_previous_claims)
      setCanClaim(data.can_claim)
      setDaysLeftToClaim(data.days_left_to_claim)
      setCanClaimPrevious(data.can_claim_previous)
      if (data.previous_policy_id !== null) {
        setSelectedPreviousPolicy(data.previous_policy_id)
      }
    } catch (error) {
      console.error('Could not get old claims list', error)
    } finally {
      setShowLoader(false)
    }
  }

  useEffect(() => {
    const storedPolicy = JSON.parse(localStorage.getItem('policyData'))
    setPolicyData(storedPolicy)
  }, [])
  
  useEffect(() => {
    if (!policyData) return
  
    setPolicyStatus(
      policyData.department === 'Mutual' || policyData.department === 'GBS'
        ? 'Add Payment Request'
        : 'Add Claim'
    )
  }, [policyData])
  
  useEffect(() => {
    if (!policyData?.policy_id) return;

    let isMounted = true

    if (activeTab === 'previous') return
    const run = async () => {
      setShowLoader(true);
      try {
        await Promise.all([
          fetchClaims(),                        
          canStillClaim(policyData.policy_id), 
        ]);
      } catch (err) {
        console.error('Initial loading error:', err)
        toast.error('Failed to load claims')
      } finally {
        if (isMounted) setShowLoader(false)
      }
    }

    run()
    return () => { isMounted = false; }
  }, [policyData?.policy_id, viewOpenClaims])


  useEffect(() => {
    if (!policyData?.policy_id) return
    if (activeTab !== 'previous') return

    let isMounted = true
    const run = async () => {
      setShowLoader(true)
      try {
        await fetchPreviousClaims(viewOpenClaims)
      } catch (err) {
        console.error('Previous claims load error:', err)
        toast.error('Failed to load previous claims')
      } finally {
        if (isMounted) setShowLoader(false)
      }
    }

    run()
    return () => { isMounted = false; }
  }, [activeTab, policyData?.policy_id, viewOpenClaims])

  
  // useEffect(() => {
  //   const initLoad = async () => {
  //     if (!policyData?.policy_id) return;
  
  //     setShowLoader(true);
     
  //     try {
  //       if(viewOpenClaims) {
        
  //       }
  //       await Promise.all([
  //         fetchClaims(),
  //         canStillClaim(policyData.policy_id)
  //       ]);
  
  //       if (activeTab === 'previous' && selectedPreviousPolicy) {
  //         await fetchPreviousClaims();
  //       }
  //     } catch (error) {
  //       toast.error("Initial loading error:", error);
  //     } finally {
  //       setShowLoader(false);
  //     }
  //   };
  
  //   initLoad();
  // }, [policyData?.policy_id, viewOpenClaims, activeTab, selectedPreviousPolicy]);
  

  const handleTabChange = (tab) => {
    setActiveTab(tab)
  
    const requestType = policyData?.department === 'Mutual' || policyData?.department === 'GBS'
      ? 'Payment Request'
      : 'Claim'
  
    if (tab === 'current') {
      setViewOpenClaims(true)
      setPolicyStatus(`Add ${requestType}`)
      localStorage.setItem('policy_id', policyData?.policy_id || '')
    }
  
    if (tab === 'previous') {
      setViewOpenClaims(true)
      setPolicyStatus(`Add ${requestType} To Previous Scheme`)
      localStorage.setItem('policy_id', selectedPreviousPolicy || '')
    }
  }
  

  const filteredClaims = (activeTab === 'current' ? claims : previousClaims).filter(claim => {
    const query = searchQuery.toLowerCase()
    return (
      claim.claim_reference?.toLowerCase().includes(query) ||
      claim.claim_staff_name?.toLowerCase().includes(query)
    )
  })
  
  return (
    <div className="w-full">
      <div className="mb-8">
        <h3 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white text-center pt-6">
          View and Log { policyData?.department === "Mutual" || policyData?.department === "GBS" ? 'Payment Request' : 'Claims' }
        </h3>
        <h4 className="text-lg font-medium text-white text-center">
          Welcome to your Staff Absence Portal. Here you can manage your policy and administer claims.
        </h4>
      </div>
      {/* Main Content Layout */}
      <div className="flex flex-col lg:flex-row max-w-[90rem] mx-auto lg:space-x-6 mt-24">
        {/* Sidebar Card */}
        <SideBar />
        {/* Main Card Section */}
        <main className="w-full lg:w-4/5 space-y-4">
          <div className="bg-white rounded-xl shadow-md p-6 w-full">
            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-4">
              {canClaim === true && (
                <>
                  <button
                    onClick={() => handleTabChange('current')}
                    className={`px-4 py-2 font-semibold ${activeTab === 'current' ? 'border-b-4 border-sas text-sas' : 'text-gray-500'}`}
                  >
                    Current {policyData?.department === "Mutual" || policyData?.department === "GBS" ? 'Scheme' : 'Policy'}
                  </button>

                  {showOld && (
                    <button
                      onClick={() => handleTabChange('previous')}
                      className={`px-4 py-2 font-semibold ${activeTab === 'previous' ? 'border-b-4 border-sas text-sas' : 'text-gray-500'}`}
                    >
                      Previous {policyData?.department === "Mutual" || policyData?.department === "GBS" ? 'Scheme' : 'Policy'}
                    </button>
                  )}
                </>
              )}
            </div>
            {/* Filters */}
            <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
              <div className="flex items-center space-x-3">
                <div
                  onClick={() => setViewOpenClaims(prev => !prev)}
                  className={`relative inline-block w-12 h-6 transition duration-300 ease-in-out`}
                >
                  <div
                    className={`absolute inset-0 rounded-full transition ${
                      viewOpenClaims ? 'bg-sas' : 'bg-gray-300'
                    }`}
                  ></div>
                  <span
                    className={`absolute left-0 top-0 w-6 h-6 bg-white rounded-full shadow transform transition-transform duration-300 ${
                      viewOpenClaims ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  ></span>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Viewing your {viewOpenClaims ? 'open' : 'closed'} { policyData?.department === "Mutual" || policyData?.department === "GBS" ? 'Payment Request' : 'Claims' }
                </span>
              </div>           
              {((activeTab === 'current' && canClaim) || (activeTab === 'previous' && canClaimPrevious && daysLeftToClaim > 0)) && (
                <Link
                  to="/sai/claims/new"
                  className="inline-flex items-center border border-sas text-sas rounded-md overflow-hidden"
                >
                  <span className="px-4 py-2 text-sm font-medium">{policyStatus}</span>
                  <span className="bg-sas text-white px-3 py-2">
                    <FontAwesomeIcon icon={faPlus} />
                  </span>
                </Link>
              )}
            </div>

            <div className="mb-4 relative w-full sm:w-72">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter claim reference or staff name"
                className="border border-gray-300 text-xs rounded-md pl-10 pr-3 py-1 w-full focus:outline-none focus:ring-1 focus:ring-sas focus:border-sas"
              />
            </div>
            {/* Table */}
            <div className="overflow-x-auto rounded-lg">
              <table className="min-w-full text-[10px] text-left text-gray-800 overflow-hidden">
                <thead>
                  <tr className="bg-sas text-white text-xs text-start">
                    <th className="p-2 font-semibold">Reference</th>
                    <th className="p-2 font-semibold">Name</th>
                    <th className="p-2 font-semibold">Condition</th>
                    <th className="px-1 font-semibold">First Day of Absence</th>
                    <th className="font-semibold">Last Day of Absence</th>
                    <th className="px-1 font-semibold">Next Payment</th>
                    <th className="p-2 font-semibold">Status</th>
                    <th className="p-2 font-semibold">Logged</th>
                    <th className="p-2 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr className="border-b">
                      <td colSpan="9" className="text-center py-6 text-gray-500">
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                        Loading { policyData.department === 'Mutual' || policyData.department === 'GBS' ? 'Payment Requests': 'Claims'}...
                      </td>
                    </tr>
                  ) : filteredClaims.length > 0 ? (
                    filteredClaims.map((claim, idx) => (
                      <tr key={idx} className="border-b text-center">
                        <td className="p-2">{claim.claim_reference}</td>
                        <td className="py-2">{claim.claim_staff_name}</td>
                        <td className="py-2 whitespace-normal break-words">{claim.condition}</td>
                        <td className="py-2">{moment(claim.start_date).format('DD/MM/YYYY')}</td>
                        <td className="py-2">  {claim.end_date ? moment(claim.end_date).format('DD/MM/YYYY') : ''}</td>
                        <td className="py-2">{claim.next}</td>
                        <td className="py-2">
                          <span
                            data-tooltip-id={`status-tooltip-${claim.claim_id}`}
                            data-tooltip-content={claim.claim_status_text}
                            className="cursor-help"
                          >
                            {getStatusTag(claim.claim_status_id, claim.status?.text || "")}
                          </span>
                          <Tooltip id={`status-tooltip-${claim.claim_id}`} place="top" />
                        </td>
                        <td className="py-2">{moment(claim.created_at).format('DD/MM/YYYY')}</td>
                        <td className="py-2 space-x-1">
                          {viewOpenClaims && (
                            <>
                              {policyData?.department === "Mutual" || policyData?.department === "GBS" ? (
                                claim.claim_status_id < 3 || claim.claim_status_id === 12 ? (
                                  <button
                                    type="button"
                                    onClick={() => openEdit(claim)}
                                    className="text-sas hover:text-sas-dark font-medium hover:underline"
                                  >
                                    <FontAwesomeIcon icon={faPencil} className="h-3 w-3" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => openClaim(claim)}
                                    className="text-sas hover:text-sas-dark font-medium hover:underline"
                                  >
                                    <FontAwesomeIcon icon={faWrench} className="h-3 w-3" />
                                  </button>
                                )
                              ) : claim.claim_status_id < 6 || claim.claim_status_id === 12 ? (
                                <button
                                  type="button"
                                  onClick={() => openEdit(claim)}
                                  className="text-sas hover:text-sas-dark font-medium hover:underline"
                                >
                                  <FontAwesomeIcon icon={faPencil} className="h-3 w-3" />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => openClaim(claim)}
                                  className="text-sas hover:text-sas-dark font-medium hover:underline"
                                >
                                  <FontAwesomeIcon icon={faWrench} className="h-3 w-3" />
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="border-b">
                      <td colSpan="9" className="text-center text-gray-500 py-4">
                        {policyData?.department === "Mutual" || policyData?.department === "GBS"
                          ? "No Payment Request Found"
                          : "No Claims Found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <EditClaimSlideOut
              toggleClaim={toggleClaim}
              setToggleClaim={setToggleClaim}
              claim={claim}
              policy={policyData}
              onRefresh={fetchClaims} 
          />
          <ClaimSlideOut
              open={open}
              setOpen={setOpen}
              claim={claim}
              policy={policyData}
          />
        </main>
      </div>
    </div>
  )
}

export default Claims
